import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil, Star } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type Testimonial } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { ConfirmDelete } from "@/components/admin/confirm-delete"
import { ImageUpload } from "@/components/admin/image-upload"

export const Route = createFileRoute("/admin/_authed/testimonials")({
  component: TestimonialsPage,
})

type FormState = { client_name: string; client_role: string; quote: string; rating: number; order: number; is_active: boolean }
const empty: FormState = { client_name: "", client_role: "", quote: "", rating: 5, order: 0, is_active: true }

function TestimonialsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<Testimonial[]>({
    queryKey: ["admin", "testimonials"],
    queryFn: async () => (await api.get<ApiEnvelope<Testimonial[]>>("/admin/testimonials")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Testimonial | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [avatar, setAvatar] = useState<File | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "testimonials"] })
    queryClient.invalidateQueries({ queryKey: ["testimonials"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v)))
      if (avatar) fd.append("avatar", avatar)
      if (editing) {
        fd.append("_method", "PUT")
        await api.post(`/admin/testimonials/${editing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      } else {
        await api.post("/admin/testimonials", fd, { headers: { "Content-Type": "multipart/form-data" } })
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Testimonial updated." : "Testimonial created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/testimonials/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("Testimonial deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setAvatar(null)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (item: Testimonial) => {
    setEditing(item)
    setForm({
      client_name: item.client_name,
      client_role: item.client_role ?? "",
      quote: item.quote,
      rating: item.rating,
      order: item.order,
      is_active: item.is_active,
    })
    setAvatar(null)
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Testimonials</h1>
          <p className="mt-1 text-sm text-muted-foreground">Client quotes shown on the home page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New testimonial
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit testimonial" : "New testimonial"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="client_name">Client name</Label>
                  <Input
                    id="client_name"
                    value={form.client_name}
                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    aria-invalid={!!fieldErrors.client_name}
                  />
                  <FieldError message={fieldErrors.client_name} />
                </div>
                <div>
                  <Label htmlFor="client_role">Client role</Label>
                  <Input
                    id="client_role"
                    value={form.client_role}
                    onChange={(e) => setForm({ ...form, client_role: e.target.value })}
                    aria-invalid={!!fieldErrors.client_role}
                  />
                  <FieldError message={fieldErrors.client_role} />
                </div>
              </div>
              <div>
                <Label htmlFor="quote">Quote</Label>
                <Textarea
                  id="quote"
                  value={form.quote}
                  onChange={(e) => setForm({ ...form, quote: e.target.value })}
                  aria-invalid={!!fieldErrors.quote}
                />
                <FieldError message={fieldErrors.quote} />
              </div>
              <ImageUpload label="Avatar" currentUrl={editing?.avatar_url} onChange={setAvatar} rounded="rounded-full" />
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="rating">Rating (1-5)</Label>
                  <Input
                    id="rating"
                    type="number"
                    min={1}
                    max={5}
                    value={form.rating}
                    onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} id="is_active" />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="text-sm text-destructive">Couldn't load testimonials.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(data ?? []).map((t) => (
              <div key={t.id} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-start justify-between">
                  <div className="flex gap-1 text-rust">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`h-3.5 w-3.5 ${i < t.rating ? "fill-rust" : "opacity-25"}`} />
                    ))}
                  </div>
                  <Badge variant={t.is_active ? "default" : "secondary"}>{t.is_active ? "Active" : "Hidden"}</Badge>
                </div>
                <p className="mt-3 text-sm text-muted-foreground line-clamp-3">"{t.quote}"</p>
                <p className="mt-3 text-sm font-semibold">{t.client_name}</p>
                {t.client_role && <p className="text-xs text-muted-foreground">{t.client_role}</p>}
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(t)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <ConfirmDelete itemLabel={t.client_name} onConfirm={() => remove.mutate(t.id)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
